import {
  MAX_CHORD_LENGTH,
  MAX_SAGITTA,
  MAX_WALL_LENGTH,
  MIN_CHORD_LENGTH,
  MIN_SAGITTA,
  MIN_WALL_LENGTH
} from '@/editor/constant/wall';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { RulerGuideLineModel } from '@/editor/entities/2d/rulerGuideLineModel';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { globalStores } from '@/editor/stores';
import { getRelativeWallRulerInfos } from '@/editor/stores/utils/ruler/wall';
import { EWallOperateType } from '@/editor/types';
import { clamp } from 'lodash-es';

type IWallRulerInfo = ReturnType<typeof getRelativeWallRulerInfos>[number];

export class WallRulerController {
  private _mainWallId: string = '';
  private _chordGuideLineModel = new RulerGuideLineModel();
  private _rulerModel = new RulerModel<ReturnType<typeof getRelativeWallRulerInfos>[number]>({
    onCommit: this.rulerOnChange.bind(this),
    onChange: this.rulerOnChange.bind(this)
  });

  constructor(wallId: string) {
    this._mainWallId = wallId;
  }

  // 标尺处理
  update() {
    if (!this._mainWallId) return;

    const mainWallData = globalStores.wallStore.getWallData(this._mainWallId);
    if (mainWallData?.baseData.center) {
      this._chordGuideLineModel.update([{ start: mainWallData.baseData.start, end: mainWallData.baseData.end }]);
    } else {
      this._chordGuideLineModel.clear();
    }

    this._rulerModel.updateModel(
      getRelativeWallRulerInfos(this._mainWallId).map((item, index) => ({
        ...item,
        id: `${index}`,
        focusTrap: true,
        blurOnCommit: true
      }))
    );
  }

  private rulerOnChange(id: string, value: number, rulerInfo: IWallRulerInfo) {
    const rulerWallData = globalStores.wallStore.getWallData(rulerInfo.wallId);
    const mainWallData = globalStores.wallStore.getWallData(this._mainWallId);
    if (!rulerWallData || !mainWallData) return;

    const mainWallStart = mainWallData.baseData.start;
    const mainWallEnd = mainWallData.baseData.end;
    const oldDistance = Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end);
    const newDistance = value;
    const diffDistance = newDistance - oldDistance;

    // 主墙体端点变化
    if (rulerInfo.wallId === mainWallData.id) {
      const isArc = mainWallData.baseData.center && mainWallData.baseData.clockwise !== undefined;
      const clampedDiffDistance = isArc
        ? clamp(value, MIN_CHORD_LENGTH, MAX_CHORD_LENGTH) - oldDistance
        : clamp(value, MIN_WALL_LENGTH, MAX_WALL_LENGTH) - oldDistance;
      const clampedSagitta = isArc && rulerInfo.arcMidMovable ? clamp(value, MIN_SAGITTA, MAX_SAGITTA) : diffDistance;
      const direction = Point2dUtils.subtracted(mainWallData.baseData.end, mainWallData.baseData.start);
      let pending: Promise<unknown> = Promise.resolve();
      if (rulerInfo.arcMidMovable && mainWallData.baseData.center && mainWallData.baseData.clockwise !== undefined) {
        const currentArc = new ArcSeg2d({
          start: mainWallStart,
          end: mainWallEnd,
          center: mainWallData.baseData.center,
          clockwise: mainWallData.baseData.clockwise
        });
        const newSagitta = clampedSagitta * Math.sign(currentArc.getSagitta());
        const newCenter = new ArcSeg2d({ start: mainWallStart, end: mainWallEnd, sagitta: newSagitta }).center;
        pending = globalStores.wallOperation.modifyWallBaseData(rulerInfo.wallId, { center: newCenter });
      } else if (rulerInfo.endMovable) {
        const newEndPoint = Point2dUtils.addDistance(mainWallEnd, clampedDiffDistance, direction);
        pending = globalStores.wallOperation.moveWallPoint(rulerInfo.wallId, {
          type: EWallOperateType.EndPoint,
          point: newEndPoint
        });
      } else if (rulerInfo.startMovable) {
        const newStartPoint = Point2dUtils.addDistance(mainWallStart, -clampedDiffDistance, direction);
        pending = globalStores.wallOperation.moveWallPoint(rulerInfo.wallId, {
          type: EWallOperateType.StartPoint,
          point: newStartPoint
        });
      }

      // 更新标尺：等操作落到 store 之后再重画，否则标尺会用旧数据
      void pending.then(() => this.update());

      return;
    }

    // 平移墙体
    const mainWallSeg = new LineSeg2d(mainWallStart, mainWallEnd);
    const rulerMidPoint = Point2dUtils.getCenter(rulerInfo.start, rulerInfo.end);
    const side = Math.sign(mainWallSeg.pointLineSegSide(rulerMidPoint));
    const mainWallVerticalDirection = mainWallSeg.getPerpendicularDirectionVector();

    const rulerDirection = Point2dUtils.getUnitDirection(rulerInfo.start, rulerInfo.end);

    if (mainWallData.baseData.center) {
      // 圆弧墙标尺修改时，平移方向是标尺方向
      const moveDirection = rulerInfo.startMovable ? { x: -rulerDirection.x, y: -rulerDirection.y } : rulerDirection;
      void globalStores.wallOperation.pan(mainWallData.id, diffDistance, moveDirection).then(() => this.update());
    } else {
      const moveDistance =
        -Math.abs(Point2dUtils.projectScalar(mainWallVerticalDirection, rulerDirection)) * diffDistance * side;
      void globalStores.wallOperation.pan(mainWallData.id, moveDistance).then(() => this.update());
    }
  }

  dispose() {
    this._chordGuideLineModel.dispose();
    this._rulerModel.dispose();
  }
}
